import React, { useContext, useEffect, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { PermissionsEnum } from '@/api/Identity/Types/PermissionsEnum'
import { PageData } from '@/api/Pagination/Types'
import { RewardProgramDetailsUpdateDto } from '@/api/Trading-Accounts/Types/RewardProgramDto'
import {
  RewardProgramDto,
  RewardsHubTab,
  RewardStatuses,
} from '@/api/Trading-Accounts/Types/RewardsHubDto'
import AddButton from '@/components/AddButton/AddButton'
import { Pager } from '@/components/components/Pager'
import { FilterType } from '@/components/Filter'
import { SelectFiltersFacade } from '@/components/Filter/ClientFields/SelectFiltersFacade'
import { FilterCell } from '@/components/Filter/FilterCell'
import { Link } from '@/components/Link'
import { Loading } from '@/components/Loading'
import MoreButton from '@/components/MoreButton/MoreButton'
import { successToast, ToastContext } from '@/components/Toast/Toast'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { AddNewRewardProgram } from '@/routes/Marketing/RewardsHub/RewardPrograms/AddNewRewardProgram'
import { rewardHubProgramsColumns } from '@/routes/Marketing/RewardsHub/RewardPrograms/RewardHubProgramsColumns'
import { rewardProgramsFilteringFields } from '@/routes/Marketing/RewardsHub/RewardPrograms/rewardProgramsFilteringFields'
import { useRewardProgramsFiltering } from '@/routes/Marketing/RewardsHub/RewardPrograms/useRewardProgramsFiltering'
import { RewardsHubNavigation } from '@/routes/Marketing/RewardsHub/RewardsHubNavigation'
import { useAppDispatch, useAppSelector } from '@/store'
import { GridFilterActionState } from '@/store/grid'
import {
  fetchRewardsCategories,
  fetchRewardTrackingMetrics,
} from '@/store/trading-accounts/trading-accounts.actions'
import { useApiClient } from '@/utils/api/ApiClient'
import { TradingAccountsApiClient } from '@/utils/api/tradingAccountsApi'
import { useCallbackWithForceRefresh } from '@/utils/api/useCallbackWithForceRefresh'
import { useFetchOneWithFetchingUpdate } from '@/utils/api/useFetchOneWithFetchingUpdate'
import { AuthSessionContext } from '@/utils/AuthContext'
import { links } from '@/utils/featureToggles'
import { isEmpty } from '@/utils/is-empty'
import { hasPermission } from '@/utils/permissionsValidator'
import { TableStateContext } from '@/utils/TableStateContext'
import { GridColumn as Column, Grid, GridCellProps, GridColumn } from '@progress/kendo-react-grid'

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  return {
    fetchRewardTrackingMetrics: async () =>
      dispatch(fetchRewardTrackingMetrics(tradingAccountsApiClient)),
  }
}

export const RewardPrograms: React.FC = () => {
  const { fetchRewardTrackingMetrics } = useMapDispatchToProps()
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const rewardTrackingMetricsResult = useAppSelector(
    (state) => state.tradingAccounts.rewardTrackingMetrics
  )

  useEffect(() => {
    const fetchData = async () => {
      if (isEmpty(rewardTrackingMetricsResult)) {
        await fetchRewardTrackingMetrics()
      }
    }

    fetchData().catch(console.error)
  }, [rewardTrackingMetricsResult, fetchRewardsCategories])

  const {
    tableData,
    setTableData,
    data,
    setData,
    getSort,
    gridState,
    handleSkipCountChange,
    handleSortChange,
    handleGridPageChange,
    handlePageSizeChange,
    gridStatusState,
    gridStatusActions,
    gridActions,
    filterFetch,
    getQuery,
    fetchGrid,
  } = useRewardProgramsFiltering()

  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const { callback: rewardsTrackingMetricCallback } = useCallbackWithForceRefresh(() => {
    return tradingAccountsApiClient.getRewardTrackingMetrics()
  }, [])
  const { callback: rewardsCallback } = useCallbackWithForceRefresh(() => {
    return tradingAccountsApiClient.getRewards({
      pageSize: 1000,
    })
  }, [])
  const { callback: rewardsProgramCallback } = useCallbackWithForceRefresh(() => {
    return tradingAccountsApiClient.getRewardPrograms({
      pageSize: 1000,
    })
  }, [])

  const { data: rewardsList } = useFetchOneWithFetchingUpdate(rewardsCallback)
  const { data: rewardsProgramList } = useFetchOneWithFetchingUpdate(rewardsProgramCallback)
  const setToast = useContext(ToastContext)
  const { isLoading } = useFetchOneWithFetchingUpdate(rewardsTrackingMetricCallback)
  const [isModalOpen, setModalOpen] = useState(false)

  const handleToggleStatus = async (id: string) => {
    // 1. Fetch full details using the same API call as the detail page
    const fullDetails = await tradingAccountsApiClient.getRewardProgramDetails(id)

    if (!fullDetails) return

    const newStatusId = fullDetails.rewardStatus.id === RewardStatuses.DISABLED ? 1 : 2

    // 2. Build payload with full details, only changing the status
    const payload: RewardProgramDetailsUpdateDto = {
      id: fullDetails.id,
      internalName: fullDetails.internalName,
      displayName: fullDetails.displayName,
      description: fullDetails.description,
      displayOrder: fullDetails.displayOrder,
      numberOfTiers: fullDetails.numberOfTiers,
      lastModified: fullDetails.lastModified,
      startDate: fullDetails.startDate,
      endDate: fullDetails.endDate,
      rewardStatusId: newStatusId, // ← only this changes
      resetCadenceId: fullDetails.resetCadence?.id,
      companies: fullDetails.companies?.map((c) => c.id),
      countries: fullDetails.countries?.map((c) => c.id),
      tiers: fullDetails.tiers?.map((tier) => ({
        id: tier.id,
        tierNumber: tier.tierNumber,
        name: tier.name,
        thresholdValue: tier.thresholdValue,
        callToActionText: tier.callToActionText,
        currencyId: tier.trackingMetric?.id === 1 ? null : tier.currency?.id,
        trackingMetricId: tier.trackingMetric?.id,
        rewardProgramTierConditionLogicId: tier.rewardProgramTierConditionLogic?.id,
        rewardId: tier.reward?.id,
        metrics: (tier.metrics ?? []).map((metric) => ({
          id: metric.id,
          value: metric.value,
          currencyId: metric.trackingMetric?.id === 1 ? null : metric.currency?.id,
          rewardProgramTierLogicalOperatorId: metric.rewardProgramTierLogicalOperator?.id,
          rewardProgramTierConditionLogicId: metric.rewardProgramTierConditionLogic?.id,
          trackingMetricId: metric.trackingMetric?.id,
        })),
      })),
    }

    await tradingAccountsApiClient.updateRewardProgramDetails(fullDetails.id, payload)
    setToast(successToast(`Program ${newStatusId === 1 ? 'activated' : 'disabled'}.`))
    await loadGrid()
  }

  const loadGrid = async () => {
    await fetchGrid(
      async (gridFilterState: GridFilterActionState): Promise<PageData<RewardProgramDto[]>> => {
        const searchQuery = getQuery(gridFilterState.filter)
        const sortQuery = getSort(gridFilterState.sort)
        const response = await tradingAccountsApiClient.getRewardPrograms({
          search: searchQuery,
          sort: sortQuery,
          pageIndex: gridFilterState.pageIndex,
          pageSize: gridFilterState.pageSize,
          skipCount: tableData?.skipCount !== false,
        })

        if (response?.items) setData(response.items)
        setTableData({ ...tableData, meta: response })
        return response
      }
    )
  }

  const onSubmit = async (values: RewardProgramDetailsUpdateDto) => {
    const cleaned: RewardProgramDetailsUpdateDto = {
      ...values,
      tiers: values.tiers?.map((tier) => ({
        ...tier,
        currencyId: tier.trackingMetricId === 1 ? null : tier.currencyId,
        metrics: tier.metrics?.map((metric) => ({
          ...metric,
          currencyId: metric.trackingMetricId === 1 ? null : metric.currencyId,
        })),
      })),
    }

    await tradingAccountsApiClient.addRewardProgram(cleaned)
    setToast(successToast('Reward Program created.'))
    setModalOpen(false)
    await loadGrid()
  }

  useEffect(() => {
    loadGrid()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <RewardsHubNavigation tab={RewardsHubTab.PROGRAMS} />
      <AddNewRewardProgram
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={onSubmit}
        rewardsList={rewardsList}
        rewardProgramList={rewardsProgramList}
      />
      <Breadcrumbs crumbs={[{ title: 'Reward Programs', link: links.rewardPrograms.url }]} />
      <SelectFiltersFacade
        gridState={gridState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetch}
        filterType={FilterType.MARKETING_CAMPAIGNS_OVERVIEW} //to change this
        filterFields={rewardProgramsFilteringFields()}
        getQuery={getQuery}
      />

      <Loading isLoading={isLoading}>
        <AddButton
          text='Create New Program'
          disabled={!hasPermission(permissions, PermissionsEnum.CanAddOrEditRewards)}
          onClick={() => setModalOpen(true)}
        />
        <TableStateContext.Provider value={[tableData, setTableData]}>
          <Grid
            {...gridState}
            resizable
            data={data}
            sort={gridState.sort}
            total={gridState.total}
            take={gridState.take}
            skip={gridState.skip}
            onSortChange={handleSortChange}
            pageable
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChange}
                onPageChange={handleGridPageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex}
                pageSize={gridState.pageSize}
                take={gridState.take || 10}
                skip={gridState.skip || 0}
              />
            )}
            style={{ zIndex: 0 }}
          >
            <Column
              title='Program name (internal)'
              cell={(props) => {
                if (!hasPermission(permissions, PermissionsEnum.CanAddOrEditRewards)) {
                  return <td>{props.dataItem.internalName}</td>
                }
                return (
                  <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
                    <Link
                      to={{
                        pathname: generatePath(links.rewardPrograms.detailsUrl as string, {
                          id: props.dataItem.id,
                          slug: 'details',
                        }),
                      }}
                    >
                      {props.dataItem.internalName}
                    </Link>
                  </td>
                )
              }}
            />

            {rewardHubProgramsColumns
              .filter((c) => c.show)
              .map((column, idx) => (
                <GridColumn key={idx} {...column} />
              ))}
            <Column
              width={50}
              filterCell={FilterCell}
              cell={(gridCellProps: GridCellProps) => (
                <td>
                  <MoreButton
                    items={[
                      {
                        name:
                          gridCellProps.dataItem.programStatus.id === RewardStatuses.DISABLED
                            ? 'Activate'
                            : 'Disable',
                        onClick: () => handleToggleStatus(gridCellProps.dataItem.id),
                      },
                    ]}
                    data-test='more-button'
                  />
                </td>
              )}
            />
          </Grid>
        </TableStateContext.Provider>
      </Loading>
    </>
  )
}
